import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { db } from '../../firebase'
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import CryptoJS from 'crypto-js'

// Función para verificar contraseña (compatibilidad con bcrypt anterior)
const verifyPassword = (plainPassword, storedHash) => {
  // Si el hash comienza con $2 (bcrypt), no podemos verificar en frontend
  if (storedHash.startsWith('$2')) {
    return null  // Retornar null para indicar que necesita actualización
  }
  
  // Comparar como texto plano o hash SHA256
  const sha256Hash = CryptoJS.SHA256(plainPassword).toString()
  return plainPassword === storedHash || sha256Hash === storedHash
}

export default function LoginAdmin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Buscar usuario por email en Firebase
      const usersRef = collection(db, 'users')
      const q = query(usersRef, where('email', '==', email.trim().toLowerCase()))
      const querySnapshot = await getDocs(q)
      
      if (querySnapshot.empty) {
        setError('Usuario o contraseña incorrectos')
        setPassword('')
        return
      }

      const userDoc = querySnapshot.docs[0].data()
      const userId = querySnapshot.docs[0].id

      const match = verifyPassword(password, userDoc.passwordhash || '')

      // Si es null, significa que es bcryptjs y necesita actualizar
      if (match === null) {
        const newPassword = prompt('Tu contraseña está en formato antiguo.\n\nPor favor ingresa una NUEVA contraseña para tu cuenta:')
        if (!newPassword) {
          setError('Operación cancelada')
          return
        }
        
        try {
          // Hashear con SHA256
          const hashedPassword = CryptoJS.SHA256(newPassword).toString()
          await updateDoc(doc(db, 'users', userId), {
            passwordhash: hashedPassword
          })
          
          alert('✅ Tu contraseña ha sido actualizada. Intenta loguearte de nuevo.')
          setPassword('')
          return
        } catch (err) {
          console.error('❌ Error actualizando contraseña:', err)
          setError('Error al actualizar contraseña: ' + err.message)
          return
        }
      }

      if (!match) {
        setError('Usuario o contraseña incorrectos')
        setPassword('')
        return
      }

      // Guardar sesión en localStorage
      localStorage.setItem('adminAuth', 'true')
      localStorage.setItem('adminLoginTime', new Date().getTime())
      localStorage.setItem('adminEmail', userDoc.email)
      const role = userDoc.role || 'admin'
      localStorage.setItem('adminRole', role)
      const dest = role === 'admin' ? '/admin' : role === 'editor' ? '/editor' : '/vendedor'
      navigate(dest)
    } catch (err) {
      setError('Error al iniciar sesión')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4" style={{background:"radial-gradient(circle at 20% 20%, rgba(34,211,238,0.12), transparent 30%), radial-gradient(circle at 80% 10%, rgba(163,230,53,0.12), transparent 28%), linear-gradient(135deg, #0b1c2c 0%, #0f172a 45%, #0a2137 100%)"}}>
      {/* Contenido del login */}
      <div className="w-full max-w-md relative">
        <div className="absolute inset-0 rounded-[28px] bg-gradient-to-br from-cyan-500/10 via-white/5 to-lime-400/10 blur-2xl"></div>

        <div className="relative rounded-[28px] border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl shadow-cyan-500/10 px-8 sm:px-10 py-10 space-y-8">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-cyan-200 text-sm font-semibold border border-white/10">
              <span className="h-2 w-2 rounded-full bg-lime-400 animate-pulse"></span>
              Acceso seguro
            </div>
            <h1 className="text-3xl font-bold text-white">Panel Administrativo</h1>
            <p className="text-gray-300 text-sm">Adrox Peru · Gestión de Productos</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            {error && (
              <div className="bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-semibold text-white">Correo</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@tuempresa.com"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-semibold text-white">Contraseña de Administrador</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingresa la contraseña"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-lime-400 hover:from-cyan-400 hover:to-lime-300 disabled:opacity-60 text-gray-900 font-bold py-3 rounded-xl transition-all shadow-lg shadow-cyan-500/30"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  Verificando...
                </>
              ) : (
                <>
                  <LockClosedIcon className="w-5 h-5" />
                  Acceder al Panel
                </>
              )}
            </button>
          </form>

          <div className="pt-4 border-t border-white/10 text-center">
            <p className="text-gray-400 text-sm leading-6">
              ¿Necesitas ayuda?<br />
              Contacta al administrador del sistema
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-white/60 text-xs">Adrox Peru © 2024</p>
        </div>
      </div>
    </div>
  )
}
